import { FunctionComponent } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { track } from '@fff-web/fff-analytics';
import { Button, Modal, showErrorToast } from '@fff-web/fff-ui-shared';
import { growthSdk } from '@fff-web/growth-sdk';
import invalidateCaches from '../../../helpers/invalidateCaches';
import { extractErrorDetails } from '../../../utils/functions/extractErrorDetails';
import { CANCEL_BOX_SUBSCRIPTION, CANCEL_CLUB_MEMBERSHIP } from '../../constants';
import envValues from '../utils/env';
import { CANCEL_FLOW_EVENT_NAME, MY_ACCOUNT_EXPERIENCE } from '../utils/segment';
import {
  CLUB_MEMBERSHIP_REACTIVATE_ERROR_CODE_LOG_MESSAGE,
  CLUB_MEMBERSHIP_REACTIVATE_ERROR_LOG_MESSAGE,
  CLUB_MEMBERSHIP_REACTIVATE_TOAST_TITLE,
  CLUB_MEMBERSHIP_REACTIVATE_UNMAPPED_ERROR_CODE_LOG_MESSAGE,
  getClubMembershipErrorToastMessage,
  getErrorDetailsFromReactivateResponse,
  hasMappedClubMembershipErrorToastMessage,
} from './clubMembershipUtils';
import * as styles from './ClubMembershipModal.module.scss';

const growthSdkInstance = growthSdk(envValues.appEnv as Parameters<typeof growthSdk>[0], true);

type ClubMembershipModalProps = {
  subscriptionId: string;
  onClose: () => void;
  variant: 'cancel' | 'reactivate';
  isOpen: boolean;
  onDone: () => void;
  isBeautyEditBoxSubscription?: boolean;
};

const getClubMembershipModalDescription = (variant: 'cancel' | 'reactivate') =>
  variant === 'reactivate'
    ? 'By clicking “Reactivate”, you authorize us to reactivate your Shopping Club Membership. You will be charged $5 at reactivation and again on the same day each month until you cancel.'
    : 'Are you sure you want to cancel your membership? You will lose any loyalty points you earned and other membership perks.';
const getKeepMembershipButtonLabel = (isBeautyEditBoxSubscription: boolean) =>
  isBeautyEditBoxSubscription ? 'Keep Subscription' : 'Keep Membership';

const boxSubscriptionModalDescription =
  'Are you sure you want to cancel your Beauty Edit Box? If you cancel now, your subscription will not renew next month.';

export const ClubMembershipModal: FunctionComponent<ClubMembershipModalProps> = ({
  isOpen,
  subscriptionId,
  onClose,
  variant,
  onDone,
  isBeautyEditBoxSubscription = false,
}) => {
  const asyncInvalidateCaches = () => {
    setTimeout(() => invalidateCaches(), 100);
  };

  const handleSkipCancelFlow = () => {
    track({
      trackName: CANCEL_FLOW_EVENT_NAME.skipped,
      properties: {
        experience: MY_ACCOUNT_EXPERIENCE,
        cancel_type: isBeautyEditBoxSubscription
          ? CANCEL_BOX_SUBSCRIPTION.type
          : CANCEL_CLUB_MEMBERSHIP.type,
        cta_name: getKeepMembershipButtonLabel(isBeautyEditBoxSubscription),
      },
    });
    onClose();
  };

  const onCancelSubscription = async () => {
    if (!subscriptionId) return;

    const response = await growthSdkInstance.cancelUserSubscription(subscriptionId);

    if (!response.ok) return;

    onClose();
    asyncInvalidateCaches();
    onDone();
  };

  const onReactivateSubscription = async () => {
    if (!subscriptionId) return;

    try {
      const response = await growthSdkInstance.reactivateUserSubscription(subscriptionId);

      if (!response.ok) {
        const { errorCode, errorMessage } = getErrorDetailsFromReactivateResponse(response);
        if (errorCode) {
          datadogLogs.logger.info(CLUB_MEMBERSHIP_REACTIVATE_ERROR_CODE_LOG_MESSAGE, { errorCode });
          if (!hasMappedClubMembershipErrorToastMessage(errorCode)) {
            datadogLogs.logger.warn(CLUB_MEMBERSHIP_REACTIVATE_UNMAPPED_ERROR_CODE_LOG_MESSAGE, {
              errorCode,
            });
          }
        }
        showErrorToast(
          CLUB_MEMBERSHIP_REACTIVATE_TOAST_TITLE,
          getClubMembershipErrorToastMessage(errorCode, errorMessage),
        );
        return;
      }

      onClose();
      asyncInvalidateCaches();
      onDone();
    } catch (error) {
      const { errorCode, errorMessage } = extractErrorDetails(error);
      if (errorCode) {
        datadogLogs.logger.info(CLUB_MEMBERSHIP_REACTIVATE_ERROR_CODE_LOG_MESSAGE, { errorCode });
        if (!hasMappedClubMembershipErrorToastMessage(errorCode)) {
          datadogLogs.logger.warn(CLUB_MEMBERSHIP_REACTIVATE_UNMAPPED_ERROR_CODE_LOG_MESSAGE, {
            errorCode,
          });
        }
      }
      const reactivateError =
        error instanceof Error ? error : new Error('Unknown club membership reactivation error');
      showErrorToast(
        CLUB_MEMBERSHIP_REACTIVATE_TOAST_TITLE,
        getClubMembershipErrorToastMessage(errorCode, errorMessage),
      );
      datadogLogs.logger.error(CLUB_MEMBERSHIP_REACTIVATE_ERROR_LOG_MESSAGE, {}, reactivateError);
    }
  };

  const onSubmit = () => {
    if (variant === 'cancel') onCancelSubscription();
    if (variant === 'reactivate') onReactivateSubscription();
  };

  const description = isBeautyEditBoxSubscription
    ? boxSubscriptionModalDescription
    : getClubMembershipModalDescription(variant);

  return (
    <Modal isOpen={isOpen} className={styles.modal}>
      <h3 className={styles.title}>
        {variant === 'reactivate'
          ? 'Reactivate Membership'
          : isBeautyEditBoxSubscription
            ? 'Cancel Subscription'
            : 'Cancel Membership'}
      </h3>
      <p className={styles.description}>{description}</p>
      <div className={styles.buttons}>
        <Button
          variation="black-outline"
          onClick={() => (variant === 'cancel' ? onSubmit() : onClose())}
        >
          {variant === 'cancel' ? 'Cancel Membership' : 'Cancel'}
        </Button>
        <Button onClick={() => (variant === 'cancel' ? handleSkipCancelFlow() : onSubmit())}>
          {variant === 'cancel'
            ? getKeepMembershipButtonLabel(isBeautyEditBoxSubscription)
            : 'Reactivate'}
        </Button>
      </div>
    </Modal>
  );
};
